import { zodResolver } from '@hookform/resolvers/zod';
import {
  useUpdateSensorConfig,
  useListUnitTypes,
  useGetUnitType,
  type SensorsSensor,
  type UnittypesUnitResponse,
  getGetSensorQueryKey,
  getListSensorsQueryKey,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Check, AlertCircle } from 'lucide-react';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { Modal, Button, Toggle, cn } from '@/components/ui';

const sensorEditSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  unitTypeId: z.string().nullable().optional(),
  unitSymbol: z.string().nullable().optional(),
  reportingInterval: z.coerce
    .number()
    .min(1, 'Must be at least 1 second')
    .max(86400, 'Cannot exceed 24 hours')
    .nullable()
    .optional()
    .or(z.literal('')),
  alertThreshold: z.coerce
    .number()
    .min(1, 'Must be at least 1 second')
    .max(604800, 'Cannot exceed 7 days')
    .nullable()
    .optional()
    .or(z.literal('')),
  isMonotonic: z.boolean().optional(),
});

type SensorEditFormData = z.infer<typeof sensorEditSchema>;

interface SensorEditModalProps {
  sensor: SensorsSensor;
  isOpen: boolean;
  onClose: () => void;
}

// Custom dropdown component for a more polished look
interface DropdownSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string; sublabel?: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

function DropdownSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled,
  error,
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          disabled && 'cursor-not-allowed opacity-50',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value || null);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === option.value && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === option.value && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {option.label}
                  </div>
                  {option.sublabel && (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">
                      {option.sublabel}
                    </div>
                  )}
                </div>
                {value === option.value && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

// Clean text input for the modal
interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  suffix?: string;
}

function FormInput({ label, error, suffix, className, ...props }: FormInputProps) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <div className="relative">
        <input
          className={cn(
            'w-full rounded-lg border px-3 py-2.5 text-sm transition-all',
            'bg-white text-gray-900 placeholder-gray-400 dark:bg-[#111113] dark:text-white dark:placeholder-zinc-500',
            'focus:outline-none focus:ring-1',
            suffix && 'pr-12',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500 dark:border-white/[0.08]',
            props.disabled && 'cursor-not-allowed opacity-50'
          )}
          {...props}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-zinc-500">
            {suffix}
          </span>
        )}
      </div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

export function SensorEditModal({ sensor, isOpen, onClose }: SensorEditModalProps) {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateSensorConfig();

  // Fetch unit types for dropdown
  const { data: unitTypesData, isLoading: isLoadingUnitTypes } = useListUnitTypes({
    query: { enabled: isOpen },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    control,
    setValue,
  } = useForm<SensorEditFormData>({
    resolver: zodResolver(sensorEditSchema),
    defaultValues: {
      name: sensor.name ?? '',
      unitTypeId: sensor.unitTypeId ?? null,
      unitSymbol: sensor.unitSymbol ?? null,
      reportingInterval: sensor.reportingInterval ?? '',
      alertThreshold: sensor.alertThreshold ?? '',
      isMonotonic: sensor.isMonotonic ?? false,
    },
  });

  // Watch the selected unit type to fetch its units
  const selectedUnitTypeId = watch('unitTypeId');

  // Fetch the selected unit type to get its units
  const { data: unitTypeData, isLoading: isLoadingUnits } = useGetUnitType(
    selectedUnitTypeId ?? '',
    {
      query: {
        enabled: isOpen && !!selectedUnitTypeId,
      },
    }
  );

  // Get available units for the selected unit type
  const availableUnits = useMemo((): UnittypesUnitResponse[] => {
    if (!selectedUnitTypeId || !unitTypeData?.units) return [];
    return unitTypeData.units;
  }, [selectedUnitTypeId, unitTypeData]);

  // Reset form when sensor changes or modal opens
  useEffect(() => {
    if (isOpen) {
      reset({
        name: sensor.name ?? '',
        unitTypeId: sensor.unitTypeId ?? null,
        unitSymbol: sensor.unitSymbol ?? null,
        reportingInterval: sensor.reportingInterval ?? '',
        alertThreshold: sensor.alertThreshold ?? '',
        isMonotonic: sensor.isMonotonic ?? false,
      });
    }
  }, [isOpen, sensor, reset]);

  const onSubmit = async (data: SensorEditFormData) => {
    if (!sensor.id) return;

    try {
      await updateMutation.mutateAsync({
        id: sensor.id,
        data: {
          name: data.name,
          unitTypeID: data.unitTypeId || undefined,
          unitSymbol: data.unitSymbol || undefined,
          reportingInterval:
            data.reportingInterval !== '' && data.reportingInterval !== null
              ? Number(data.reportingInterval)
              : undefined,
          alertThreshold:
            data.alertThreshold !== '' && data.alertThreshold !== null
              ? Number(data.alertThreshold)
              : undefined,
          isMonotonic: data.isMonotonic,
        },
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: getGetSensorQueryKey(sensor.id) });
      queryClient.invalidateQueries({ queryKey: getListSensorsQueryKey() });

      onClose();
    } catch {
      // Error is handled by the mutation state
    }
  };

  const unitTypeOptions = useMemo(() => {
    const options = [{ value: '', label: 'None', sublabel: 'No unit type assigned' }];
    if (unitTypesData?.unitTypes) {
      unitTypesData.unitTypes.forEach((ut) => {
        if (ut.id && ut.name) {
          options.push({
            value: ut.id,
            label: ut.name,
            sublabel: `${ut.unitCount ?? 0} unit${ut.unitCount === 1 ? '' : 's'}`,
          });
        }
      });
    }
    return options;
  }, [unitTypesData]);

  const unitOptions = useMemo(() => {
    const options = [{ value: '', label: 'Select a unit' }];
    availableUnits.forEach((unit) => {
      if (unit.symbol) {
        options.push({
          value: unit.symbol,
          label: `${unit.label ?? unit.symbol} (${unit.symbol})`,
        });
      }
    });
    return options;
  }, [availableUnits]);

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="sensor-edit-form"
        loading={isSubmitting || updateMutation.isPending}
      >
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Sensor"
      subtitle="Configure sensor properties and health monitoring"
      size="md"
      footer={footerContent}
    >
      <form id="sensor-edit-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <FormInput
          label="Name"
          error={errors.name?.message}
          placeholder="Enter sensor name"
          {...register('name')}
        />

        <Controller
          name="unitTypeId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DropdownSelect
              label="Unit Type"
              value={value ?? ''}
              onChange={(newValue) => {
                onChange(newValue);
                // Reset unit symbol when unit type changes
                setValue('unitSymbol', null);
              }}
              options={unitTypeOptions}
              placeholder={isLoadingUnitTypes ? 'Loading...' : 'Select unit type'}
              disabled={isLoadingUnitTypes}
              error={errors.unitTypeId?.message}
            />
          )}
        />

        {selectedUnitTypeId && (
          <Controller
            name="unitSymbol"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DropdownSelect
                label="Unit"
                value={value ?? ''}
                onChange={onChange}
                options={unitOptions}
                placeholder={isLoadingUnits ? 'Loading...' : 'Select unit'}
                disabled={isLoadingUnits}
                error={errors.unitSymbol?.message}
              />
            )}
          />
        )}

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-[#111113]">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Health Monitoring</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
            Configure when to consider the sensor stale or missing
          </p>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <FormInput
              label="Reporting Interval"
              type="number"
              error={errors.reportingInterval?.message}
              placeholder="e.g., 60"
              suffix="sec"
              {...register('reportingInterval')}
            />

            <FormInput
              label="Alert Threshold"
              type="number"
              error={errors.alertThreshold?.message}
              placeholder="e.g., 300"
              suffix="sec"
              {...register('alertThreshold')}
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-[#111113]">
          <Controller
            name="isMonotonic"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Toggle
                label="Monotonic Sensor"
                description="Auto-correct counter resets (e.g., power meters that reset to zero)"
                checked={value ?? false}
                onChange={onChange}
                layout="switch-right"
              />
            )}
          />
        </div>

        {updateMutation.isError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            Failed to update sensor. Please try again.
          </div>
        )}
      </form>
    </Modal>
  );
}
